import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Users, Plus, Trash2, Shield, User, Mail, Key, Check, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getUsers, createUser, deleteUser } from "@/lib/api";

export const UsersDialog = ({ open, onOpenChange, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  // New user form state
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("agent");
  const [submitting, setSubmitting] = useState(false);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const data = await getUsers();
      setUsers(data);
    } catch {
      toast.error("Error al cargar la lista de usuarios");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadUsers();
      setShowAdd(false);
    }
  }, [open]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password || !fullName.trim()) {
      toast.error("Por favor completa los campos requeridos");
      return;
    }

    setSubmitting(true);
    try {
      await createUser({
        username: username.trim().toLowerCase(),
        password,
        full_name: fullName.trim(),
        email: email.trim(),
        role,
      });
      toast.success("Usuario creado correctamente");
      setUsername("");
      setPassword("");
      setFullName("");
      setEmail("");
      setRole("agent");
      setShowAdd(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear usuario");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id, uname) => {
    if (!window.confirm(`¿Estás seguro de eliminar al usuario ${uname}?`)) return;
    try {
      await deleteUser(id);
      toast.success("Usuario eliminado");
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar el usuario");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-slate-800">
              <Users className="h-5 w-5 text-slate-600" />
              <DialogTitle className="font-heading text-lg">
                Gestión de Agentes y Usuarios
              </DialogTitle>
            </div>
            {!showAdd && currentUser?.role === "admin" && (
              <Button
                size="sm"
                onClick={() => setShowAdd(true)}
                className="h-8 bg-slate-900 hover:bg-slate-800 text-white gap-1.5 text-xs"
              >
                <Plus className="h-3.5 w-3.5" /> Nuevo Agente
              </Button>
            )}
          </div>
          <DialogDescription className="text-xs text-slate-500">
            Administra los accesos del equipo de asesores de viaje y roles del CRM.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Add form */}
          {showAdd && (
            <form onSubmit={handleCreate} className="p-4 rounded-xl bg-slate-50 border border-slate-200 space-y-3.5">
              <div className="flex items-center justify-between pb-2 border-b border-slate-200">
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wide">
                  Crear Nuevo Asesor / Administrador
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowAdd(false)}
                  className="text-xs h-7 text-slate-500"
                >
                  Cancelar
                </Button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-700">Nombre Completo *</label>
                  <Input
                    placeholder="Ej. Valeria Morales"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="h-9 bg-white"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-700">Nombre de Usuario *</label>
                  <Input
                    placeholder="ej. valeria_m"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="h-9 bg-white"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-700">Contraseña *</label>
                  <Input
                    type="password"
                    placeholder="••••••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-9 bg-white"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-medium text-slate-700">Correo Electrónico</label>
                  <Input
                    type="email"
                    placeholder="asesor@viajacrm.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-9 bg-white"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-slate-700">Rol:</label>
                  <Select value={role} onValueChange={setRole}>
                    <SelectTrigger className="w-40 h-8 bg-white text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agent">Agente / Asesor</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  type="submit"
                  size="sm"
                  disabled={submitting}
                  className="bg-slate-900 hover:bg-slate-800 text-white text-xs h-8"
                >
                  {submitting ? "Guardando..." : "Guardar Usuario"}
                </Button>
              </div>
            </form>
          )}

          {/* Users List */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              Usuarios Registrados ({users.length})
            </p>
            {loading ? (
              <div className="py-8 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Cargando usuarios...
              </div>
            ) : (
              <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 overflow-hidden bg-white">
                {users.map((u) => (
                  <div key={u.id} className="p-3.5 flex items-center justify-between gap-3 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="h-9 w-9 rounded-lg bg-slate-100 flex items-center justify-center text-slate-700 font-bold shrink-0 text-sm">
                        {u.full_name?.[0]?.toUpperCase() || u.username[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            {u.full_name || u.username}
                          </p>
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                              u.role === "admin"
                                ? "bg-slate-900 text-white"
                                : "bg-blue-50 text-blue-700 border border-blue-200/60"
                            }`}
                          >
                            {u.role === "admin" ? "Administrador" : "Agente"}
                          </span>
                        </div>
                        <p className="text-xs text-slate-400 truncate">
                          @{u.username} {u.email ? `· ${u.email}` : ""}
                        </p>
                      </div>
                    </div>

                    {currentUser?.role === "admin" && u.username !== "admin" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50"
                        title="Eliminar usuario"
                        onClick={() => handleDelete(u.id, u.username)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
